import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { monthLabel, fmtMoney } from '../../utils/money';
import { fmtDate } from '../../utils/dates';

// لوحة الجهة: إنشاء التقارير الشهرية وإدارة المشاريع — نقطة الإدخال الأولى للوحدة.
export default function FunderPeriods() {
  const { funderId } = useParams();
  const { api } = useAuth();
  const toast = useToast();
  const [funder, setFunder] = useState(null);
  const [periods, setPeriods] = useState(null);
  const [projects, setProjects] = useState([]);
  const [lookups, setLookups] = useState(null);
  const [addPeriod, setAddPeriod] = useState(false);
  const [addProject, setAddProject] = useState(false);
  const [editProject, setEditProject] = useState(null);

  const load = useCallback(() => {
    api('/financial/funders').then((fs) => setFunder(fs.find((f) => String(f.id) === String(funderId)) || null)).catch(() => {});
    api(`/financial/funders/${funderId}/periods`).then(setPeriods).catch((e) => toast.error(e.message));
    api(`/financial/projects?funder_id=${funderId}`).then(setProjects).catch(() => {});
    api('/financial/lookups').then(setLookups).catch(() => {});
  }, [funderId, api, toast]);
  useEffect(() => { load(); }, [load]);

  if (!periods) return <div className="loader">جاري التحميل...</div>;
  const latestMonth = periods[0]?.report_month;

  return (
    <div>
      <Link to="/financial-reports" style={{ fontSize: 13, color: 'var(--text-muted)', display: 'inline-block', marginBottom: 8 }}>← الجهات</Link>
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-title">{funder?.name || 'الجهة الممولة'}</div>
        {funder?.sponsor && (
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
            مرتبطة بالداعم في المنصّة: <strong>{funder.sponsor.name}</strong>
          </div>
        )}
        <p style={{ color: 'var(--text-muted)', fontSize: 13, margin: '6px 0 0' }}>
          أنشئ تقريراً شهرياً ثم أضِف المشاريع وعقودها وبنودها.
        </p>
      </div>

      {funder && <PortalSettings funderId={funderId} funder={funder} lookups={lookups} api={api} toast={toast} onSaved={load} />}

      {/* ===== التقارير الشهرية ===== */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
          <div className="card-title">التقارير الشهرية</div>
          <button className="btn btn-primary btn-sm" onClick={() => setAddPeriod((v) => !v)}>＋ إضافة تقرير</button>
        </div>

        {addPeriod && (
          <PeriodForm
            onCancel={() => setAddPeriod(false)}
            onSubmit={async (vals) => {
              try {
                const r = await api(`/financial/funders/${funderId}/periods`, { method: 'POST', body: JSON.stringify(vals) });
                toast.success('تم إنشاء التقرير');
                setAddPeriod(false);
                load();
                return r;
              } catch (e) { toast.error(e.message); }
            }}
          />
        )}

        {periods.length === 0 ? (
          <div className="empty">لا تقارير بعد — أضِف أول تقرير شهري.</div>
        ) : (
          <div className="table-wrap" style={{ marginTop: addPeriod ? 12 : 0 }}>
            <table>
              <thead><tr><th>الشهر</th><th>رقم الخطاب</th><th>تاريخ القطع</th><th>الحالة</th></tr></thead>
              <tbody>
                {periods.map((p) => (
                  <tr key={p.id}>
                    <td><Link to={`/financial-reports/${funderId}/${p.report_month}`}>{monthLabel(p.report_month)}</Link></td>
                    <td>{p.letter_no || '—'}</td>
                    <td>{p.cutoff_date ? fmtDate(p.cutoff_date) : '—'}</td>
                    <td>{p.status === 'published' ? <span className="badge badge-completed">منشور</span> : <span className="badge badge-pending">مسودّة</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ===== المشاريع ===== */}
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
          <div className="card-title">المشاريع</div>
          <button className="btn btn-outline btn-sm" onClick={() => setAddProject((v) => !v)}>＋ إضافة مشروع</button>
        </div>

        {addProject && (
          <ProjectForm
            lookups={lookups}
            onCancel={() => setAddProject(false)}
            onSubmit={async (vals) => {
              try {
                await api('/financial/projects', { method: 'POST', body: JSON.stringify({ ...vals, funder_id: funderId }) });
                toast.success('تم إنشاء المشروع');
                setAddProject(false);
                load();
              } catch (e) { toast.error(e.message); }
            }}
          />
        )}

        {editProject && (
          <ProjectForm
            initial={editProject}
            lookups={lookups}
            onCancel={() => setEditProject(null)}
            onSubmit={async (vals) => {
              try {
                await api(`/financial/projects/${editProject.id}`, { method: 'PUT', body: JSON.stringify(vals) });
                toast.success('تم تحديث المشروع');
                setEditProject(null);
                load();
              } catch (e) { toast.error(e.message); }
            }}
          />
        )}

        {projects.length === 0 ? (
          <div className="empty">لا مشاريع بعد.</div>
        ) : (
          <div className="table-wrap" style={{ marginTop: addProject ? 12 : 0 }}>
            <table>
              <thead><tr><th>المشروع</th><th>وحدة القياس</th><th>الحالة</th><th>التمويل/المعتمد</th><th></th></tr></thead>
              <tbody>
                {projects.map((p) => (
                  <tr key={p.id}>
                    <td style={{ fontWeight: 600 }}>{p.name}</td>
                    <td>{p.unit_basis || '—'}</td>
                    <td>{p.funding_status === 'funded' ? <span className="badge badge-completed">مموَّل</span> : <span className="badge badge-pending">معتمد بانتظار التمويل</span>}</td>
                    <td>{fmtMoney(p.funding_status === 'funded' ? p.funding_total : p.approved_amount)}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <button className="btn btn-sm btn-outline" onClick={() => setEditProject(p)}>✎ بيانات</button>{' '}
                      {latestMonth && p.funding_status === 'funded'
                        ? <Link className="btn btn-sm btn-outline" to={`/financial-reports/${funderId}/${latestMonth}/projects/${p.id}/edit`}>عقود {monthLabel(latestMonth)} ←</Link>
                        : <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{!latestMonth ? 'أنشئ تقريراً أولاً' : 'معتمد'}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function PeriodForm({ onSubmit, onCancel }) {
  const [v, setV] = useState({ month: '', cutoff_date: '', letter_no: '', letter_date_hijri: '', status: 'draft' });
  const set = (k, val) => setV((s) => ({ ...s, [k]: val }));
  const submit = () => {
    const body = { report_month: `${v.month}-01`, cutoff_date: v.cutoff_date, status: v.status };
    if (v.letter_no) body.letter_no = v.letter_no;
    if (v.letter_date_hijri) body.letter_date_hijri = v.letter_date_hijri;
    onSubmit(body);
  };
  return (
    <div style={{ background: 'var(--surface-alt)', padding: 12, borderRadius: 6 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10 }}>
        <F label="الشهر *"><input type="month" className="form-input" value={v.month} onChange={(e) => set('month', e.target.value)} /></F>
        <F label="تاريخ القطع *"><input type="date" className="form-input" value={v.cutoff_date} onChange={(e) => set('cutoff_date', e.target.value)} /></F>
        <F label="رقم الخطاب"><input className="form-input" value={v.letter_no} onChange={(e) => set('letter_no', e.target.value)} /></F>
        <F label="التاريخ الهجري"><input className="form-input" placeholder="مثال: 8 جمادى الآخرة 1447" value={v.letter_date_hijri} onChange={(e) => set('letter_date_hijri', e.target.value)} /></F>
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
        <button className="btn btn-primary btn-sm" onClick={submit} disabled={!v.month || !v.cutoff_date}>حفظ</button>
        <button className="btn btn-outline btn-sm" onClick={onCancel}>إلغاء</button>
      </div>
    </div>
  );
}

function ProjectForm({ initial = {}, lookups, onSubmit, onCancel }) {
  const [v, setV] = useState({
    name: initial.name || '', unit_basis: initial.unit_basis || '',
    technical_url: initial.technical_url || '',
    ops_project_id: initial.ops_project_id || '',
    funding_status: initial.funding_status || 'funded',
    funding_total: initial.funding_total ?? '', approved_amount: initial.approved_amount ?? '',
  });
  const set = (k, val) => setV((s) => ({ ...s, [k]: val }));
  const funded = v.funding_status === 'funded';
  const submit = () => {
    const body = { name: v.name, funding_status: v.funding_status };
    body.unit_basis = v.unit_basis || null;
    body.technical_url = v.technical_url || null;
    body.ops_project_id = v.ops_project_id || null;
    if (funded) body.funding_total = v.funding_total === '' ? null : v.funding_total;
    if (!funded) body.approved_amount = v.approved_amount === '' ? null : v.approved_amount;
    onSubmit(body);
  };
  return (
    <div style={{ background: 'var(--surface-alt)', padding: 12, borderRadius: 6 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10 }}>
        <F label="اسم المشروع *"><input className="form-input" value={v.name} onChange={(e) => set('name', e.target.value)} /></F>
        <F label="وحدة القياس"><input className="form-input" placeholder="درس/شهر/موقع/ملف" value={v.unit_basis} onChange={(e) => set('unit_basis', e.target.value)} /></F>
        <F label="حالة التمويل">
          <select className="form-select" value={v.funding_status} onChange={(e) => set('funding_status', e.target.value)}>
            <option value="funded">مموَّل</option>
            <option value="approved_pending">معتمد بانتظار التمويل</option>
          </select>
        </F>
        {funded
          ? <F label="إجمالي التمويل"><input type="number" className="form-input" value={v.funding_total} onChange={(e) => set('funding_total', e.target.value)} /></F>
          : <F label="المبلغ المعتمد"><input type="number" className="form-input" value={v.approved_amount} onChange={(e) => set('approved_amount', e.target.value)} /></F>}
        <F label="مشروع المنصّة المرتبط (لسحب المهام)">
          <select className="form-select" value={v.ops_project_id} onChange={(e) => set('ops_project_id', e.target.value)}>
            <option value="">بدون ربط</option>
            {(lookups?.ops_projects || []).map((op) => <option key={op.id} value={op.id}>{op.title}</option>)}
          </select>
        </F>
        <F label="رابط التقرير الفني"><input className="form-input" placeholder="https://..." value={v.technical_url} onChange={(e) => set('technical_url', e.target.value)} /></F>
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
        <button className="btn btn-primary btn-sm" onClick={submit} disabled={!v.name}>حفظ</button>
        <button className="btn btn-outline btn-sm" onClick={onCancel}>إلغاء</button>
      </div>
    </div>
  );
}

function PortalSettings({ funderId, funder, lookups, api, toast, onSaved }) {
  const [v, setV] = useState({
    public_enabled: funder?.public_enabled || false,
    public_slug: funder?.public_slug || '',
    public_password: '',
    sponsor_id: funder?.sponsor_id || '',
  });
  const set = (k, val) => setV((s) => ({ ...s, [k]: val }));
  const publicUrl = v.public_slug ? `${window.location.origin}/financial/${v.public_slug}` : '';

  const save = async () => {
    try {
      const body = { public_enabled: v.public_enabled, public_slug: v.public_slug || null, sponsor_id: v.sponsor_id || null };
      if (v.public_password) body.public_password = v.public_password;
      await api(`/financial/funders/${funderId}`, { method: 'PUT', body: JSON.stringify(body) });
      toast.success('تم حفظ إعدادات العرض العام');
      setV((s) => ({ ...s, public_password: '' }));
      onSaved();
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div className="card" style={{ marginBottom: 16, borderInlineStart: '4px solid var(--info)' }}>
      <div className="card-title" style={{ marginBottom: 10 }}>صفحة العرض العامة (للداعم)</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10, alignItems: 'end' }}>
        <F label="مُفعّلة">
          <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13 }}>
            <input type="checkbox" checked={v.public_enabled} onChange={(e) => set('public_enabled', e.target.checked)} /> إتاحة العرض العام (المنشور فقط)
          </label>
        </F>
        <F label="الداعم المرتبط (من المنصّة)">
          <select className="form-select" value={v.sponsor_id} onChange={(e) => set('sponsor_id', e.target.value)}>
            <option value="">بدون ربط</option>
            {(lookups?.sponsors || []).map((sp) => <option key={sp.id} value={sp.id}>{sp.name}</option>)}
          </select>
        </F>
        <F label="المعرّف (slug)"><input className="form-input" placeholder="amir" value={v.public_slug} onChange={(e) => set('public_slug', e.target.value)} /></F>
        <F label="كلمة مرور (اختياري)"><input className="form-input" type="password" placeholder={funder?.is_protected ? '•••• (محفوظة)' : 'بلا'} value={v.public_password} onChange={(e) => set('public_password', e.target.value)} /></F>
      </div>
      {publicUrl && v.public_enabled && (
        <div style={{ fontSize: 12, marginTop: 10 }}>
          الرابط العام: <a href={publicUrl} target="_blank" rel="noreferrer">{publicUrl}</a>
        </div>
      )}
      <div style={{ marginTop: 12 }}>
        <button className="btn btn-primary btn-sm" onClick={save}>حفظ إعدادات العرض</button>
      </div>
    </div>
  );
}

function F({ label, children }) {
  return (
    <div>
      <label className="form-label" style={{ fontSize: 12 }}>{label}</label>
      {children}
    </div>
  );
}
